'use client'

import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useState,
} from 'react'
import useSWR from 'swr'

import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext'
import { getPublicFollowers } from '@/services/community'

type WorldFollowersData = {
  totalItems: number
}

type WorldCommunityContextType = {
  followersData?: WorldFollowersData
  setFollowersData: Dispatch<SetStateAction<WorldFollowersData | undefined>>
}

const WorldCommunityContext = createContext<WorldCommunityContextType | null>(null)

type WorldCommunityProviderProps = PropsWithChildren<{
  enableFollowers: boolean
}>

export function WorldCommunityProvider({ children, enableFollowers }: WorldCommunityProviderProps) {
  const { roomData } = useCheckEnterRoomContext()
  const [followersData, setFollowersData] = useState<WorldFollowersData>()
  const preferredUsername = roomData.owner.community_user_id

  useSWR(
    enableFollowers && preferredUsername
      ? `getFollowersData:${preferredUsername}`
      : null,
    () =>
      getPublicFollowers({ preferredUsername: preferredUsername! }, 0, 1).then(
        (result) => (result ? { totalItems: result.total } : undefined),
      ),
    {
      onSuccess: (data) => {
        setFollowersData(data)
      },
    },
  )

  return (
    <WorldCommunityContext.Provider value={{ followersData, setFollowersData }}>
      {children}
    </WorldCommunityContext.Provider>
  )
}

export const useWorldCommunityContext = () => {
  const value = useContext(WorldCommunityContext)

  if (value == null) {
    throw new Error('useWorldCommunityContext cannot be used outside of WorldCommunityProvider')
  }

  return value
}
